import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ContactPhone } from '@mui/icons-material';
import { Proposta, PropostaFormData, PropostaItem } from '../../types/proposta';
import {
  formatarCPFCNPJ,
  formatarCPFCNPJOnInput,
  formatarPhone,
  formatarPhoneOnInput,
  formatarValor,
  formatarValorOnInput,
  formatarMoedaParaInput,
} from '../../utils/formatToView';
import EstadosBrSelect from '../selects/EstadosBrSelect';

const FONE_DDI_BR = '55';

interface PropostaFormProps {
  open: boolean;
  onClose: () => void;
  proposta?: Proposta | null;
  onSubmit: (data: PropostaFormData) => Promise<void>;
}

const criarItemVazio = (ordem: number): PropostaItem => ({
  ordem,
  descricao: '',
  quantidade: 1,
  valor_unitario: 0,
  valor_total: 0,
});

const hoje = (): string => new Date().toISOString().slice(0, 10);

const PropostaForm: React.FC<PropostaFormProps> = ({ open, onClose, proposta, onSubmit }) => {
  const [formData, setFormData] = useState<PropostaFormData>({
    nome_cliente: '',
    cpf_cnpj: '',
    endereco: '',
    bairro: '',
    cidade: '',
    estado: '',
    complemento: '',
    numero: '',
    nome_contato: '',
    fone_contato: '',
    data_proposta: hoje(),
    data_vencimento: '',
    observacoes: '',
    itens: [criarItemVazio(1)],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (proposta) {
      setFormData({
        nome_cliente: proposta.nome_cliente || '',
        cpf_cnpj: proposta.cpf_cnpj ? formatarCPFCNPJ(proposta.cpf_cnpj) : '',
        endereco: proposta.endereco || '',
        bairro: proposta.bairro || '',
        cidade: proposta.cidade || '',
        estado: proposta.estado || '',
        complemento: proposta.complemento || '',
        numero: proposta.numero || '',
        nome_contato: proposta.nome_contato || '',
        fone_contato: proposta.fone_contato ? formatarPhone(proposta.fone_contato, FONE_DDI_BR) : '',
        data_proposta: proposta.data_proposta?.slice(0, 10) || hoje(),
        data_vencimento: proposta.data_vencimento?.slice(0, 10) || '',
        observacoes: proposta.observacoes || '',
        itens: (proposta.itens || []).map((item, i) => ({
          ordem: item.ordem ?? i + 1,
          descricao: item.descricao,
          quantidade: Number(item.quantidade),
          valor_unitario: Number(item.valor_unitario),
          valor_total: Number(item.valor_total),
        })),
      });
    } else {
      setFormData({
        nome_cliente: '',
        cpf_cnpj: '',
        endereco: '',
        bairro: '',
        cidade: '',
        estado: '',
        complemento: '',
        numero: '',
        nome_contato: '',
        fone_contato: '',
        data_proposta: hoje(),
        data_vencimento: '',
        observacoes: '',
        itens: [criarItemVazio(1)],
      });
    }
    setError(null);
  }, [proposta, open]);

  const calcularTotalItem = (qtd: number, unit: number) =>
    Math.round(qtd * unit * 100) / 100;

  const valorTotalGeral = formData.itens.reduce(
    (acc, item) => acc + calcularTotalItem(item.quantidade, item.valor_unitario),
    0
  );

  const handleChange = (field: keyof Omit<PropostaFormData, 'itens'>, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index: number, field: keyof PropostaItem, value: string | number) => {
    setFormData((prev) => {
      const itens = [...prev.itens];
      const item = { ...itens[index] };

      if (field === 'descricao') {
        item.descricao = String(value);
      } else if (field === 'quantidade') {
        item.quantidade = Number(value) || 0;
      } else if (field === 'valor_unitario') {
        item.valor_unitario = Number(value) || 0;
      }

      item.valor_total = calcularTotalItem(item.quantidade, item.valor_unitario);
      itens[index] = item;
      return { ...prev, itens };
    });
  };

  const handleItemValorUnitarioInput = (index: number, raw: string) => {
    const formatted = formatarValorOnInput(raw);
    const numero = parseFloat(formatted.replace(/\./g, '').replace(',', '.')) || 0;
    handleItemChange(index, 'valor_unitario', numero);
  };

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      itens: [...prev.itens, criarItemVazio(prev.itens.length + 1)],
    }));
  };

  const handleRemoveItem = (index: number) => {
    if (formData.itens.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      itens: prev.itens
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, ordem: i + 1 })),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.nome_cliente.trim()) {
      setError('Nome do cliente é obrigatório');
      return;
    }
    if (!formData.data_proposta) {
      setError('Data da proposta é obrigatória');
      return;
    }
    if (formData.itens.some((item) => !item.descricao.trim())) {
      setError('Todos os itens devem ter descrição');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const payload: PropostaFormData = {
        ...formData,
        itens: formData.itens.map((item, i) => ({
          ...item,
          ordem: i + 1,
          valor_total: calcularTotalItem(item.quantidade, item.valor_unitario),
        })),
      };
      await onSubmit(payload);
      onClose();
    } catch {
      setError('Erro ao salvar proposta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{proposta ? 'Editar Proposta' : 'Nova Proposta'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="subtitle2" color="primary" gutterBottom>Dados do Cliente</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField label="Nome do Cliente *" fullWidth size="small"
              value={formData.nome_cliente}
              onChange={(e) => handleChange('nome_cliente', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="CPF / CNPJ" fullWidth size="small"
              value={formData.cpf_cnpj}
              onChange={(e) => handleChange('cpf_cnpj', formatarCPFCNPJOnInput(e.target.value))} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField label="Endereço" fullWidth size="small"
              value={formData.endereco}
              onChange={(e) => handleChange('endereco', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Número" fullWidth size="small"
              value={formData.numero}
              onChange={(e) => handleChange('numero', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Bairro" fullWidth size="small"
              value={formData.bairro}
              onChange={(e) => handleChange('bairro', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Cidade" fullWidth size="small"
              value={formData.cidade}
              onChange={(e) => handleChange('cidade', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <EstadosBrSelect
              value={formData.estado}
              onChange={(e) => handleChange('estado', e.target.value)}
              label="Estado"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Complemento" fullWidth size="small"
              value={formData.complemento}
              onChange={(e) => handleChange('complemento', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Nome do Contato" fullWidth size="small"
              value={formData.nome_contato}
              onChange={(e) => handleChange('nome_contato', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Fone do Contato"
              fullWidth
              size="small"
              placeholder="(11) 99999-9999"
              value={formData.fone_contato}
              onChange={(e) => handleChange('fone_contato', formatarPhoneOnInput(e.target.value, FONE_DDI_BR))}
              InputProps={{
                startAdornment: <ContactPhone sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="primary" gutterBottom>Dados da Proposta</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField label="Data da Proposta *" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
              value={formData.data_proposta}
              onChange={(e) => handleChange('data_proposta', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Vencimento da Proposta" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
              value={formData.data_vencimento}
              onChange={(e) => handleChange('data_vencimento', e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" color="primary">Itens da Proposta</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAddItem}>Adicionar Item</Button>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={50}>Item</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell width={90}>Qtde</TableCell>
              <TableCell width={130}>Valor Unit.</TableCell>
              <TableCell width={130}>Valor Total</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {formData.itens.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <TextField fullWidth size="small" variant="standard"
                    value={item.descricao}
                    onChange={(e) => handleItemChange(index, 'descricao', e.target.value)}
                    placeholder="Descrição do item" />
                </TableCell>
                <TableCell>
                  <TextField fullWidth size="small" variant="standard" type="number"
                    inputProps={{ min: 0.01, step: 0.01 }}
                    value={item.quantidade}
                    onChange={(e) => handleItemChange(index, 'quantidade', e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField fullWidth size="small" variant="standard"
                    value={formatarMoedaParaInput(item.valor_unitario)}
                    onChange={(e) => handleItemValorUnitarioInput(index, e.target.value)} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {formatarValor(calcularTotalItem(item.quantidade, item.valor_unitario))}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}
                    disabled={formData.itens.length <= 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box textAlign="right" mt={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Valor Total: {formatarValor(valorTotalGeral)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />
        <TextField label="Observações" fullWidth multiline rows={3} size="small"
          value={formData.observacoes}
          onChange={(e) => handleChange('observacoes', e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}>
          {proposta ? 'Salvar' : 'Cadastrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropostaForm;
